import { useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/packages/utils";
import { Button } from "@/components/ui/primitives";
import { springs } from "@/components/ui/motion";
import {
  OrgContextForm,
  isCompleteOrgContext,
} from "@/components/workspace/OrgContextForm";
import type { OrgContextAnswers } from "./types";

interface StepOrgContextProps {
  answers: Partial<OrgContextAnswers>;
  onAnswersChange: (answers: Partial<OrgContextAnswers>) => void;
  onComplete: (answers: OrgContextAnswers) => void;
  loading?: boolean;
}

export function StepOrgContext({
  answers,
  onAnswersChange,
  onComplete,
  loading,
}: StepOrgContextProps) {
  const { t } = useTranslation("audits");

  const allAnswered = useMemo(() => isCompleteOrgContext(answers), [answers]);

  const handleContinue = useCallback(() => {
    if (isCompleteOrgContext(answers)) {
      onComplete(answers);
    }
  }, [answers, onComplete]);

  return (
    <div className="flex flex-col gap-6 py-4">
      <div className="text-center">
        <h3 className="text-sm font-medium text-primary mb-1">
          {t("wizard.orgContext.title")}
        </h3>
        <p className="text-[12px] text-muted">
          {t("wizard.orgContext.subtitle")}
        </p>
      </div>

      <OrgContextForm answers={answers} onChange={onAnswersChange} />

      <AnimatePresence>
        {allAnswered && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={springs.smooth}
            className="flex justify-end"
          >
            <Button
              variant="primary"
              onClick={handleContinue}
              loading={loading}
            >
              {t("wizard.actions.nextGenerateChecklist")}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
